import { useTranslation } from "react-i18next";

import { useDateConstraints } from "../hooks/useDateConstraints";
import { usePopup } from "../hooks/usePopup";

import Icons from "@/components/icons";
import MenuItem from "@/components/layout/menu/MenuItem";
import Menu from "@/components/layout/navbar/Menu";
import { EditForecastBudgetPopup } from "@/components/ui/v1/popup/EditForecastBudgetPopup";
import CurrencyCell from "@/components/ui/v1/table/CurrencyCell";
import WithMarginBadge from "@/components/ui/v1/table/WithMarginBadge";
import { Button } from "@/components/ui/v2/button";
import { translateIntegrationDisplayName } from "@/features/integrations/utils";
import { JobboardCampaignType } from "@/types/response/CampaignJobBoardMonthType";
import { CampaignTypeDTO } from "@/types/response/CampaignType";
import { TableColumn } from "@/types/table/tableProps";

export const useBudgetPlanningTableColumns = (
  selectedMonth?: string,
  selectedYear?: string
) => {
  const { canEdit } = useDateConstraints();
  const { openPopup, closePopup, resetPopup } = usePopup();
  const { t, i18n } = useTranslation();

  const selectedMonthNum = selectedMonth
    ? Number.parseInt(selectedMonth, 10)
    : new Date().getMonth() + 1;
  const selectedYearNum = selectedYear
    ? Number.parseInt(selectedYear, 10)
    : new Date().getFullYear();

  const canEditSelected = canEdit(selectedYearNum, selectedMonthNum);

  const handleSuccess = () => {
    resetPopup();
  };

  const budgetPlanningTableColumns: TableColumn<CampaignTypeDTO>[] = [
    {
      accessor: "name",
      Cell: (row: CampaignTypeDTO) => <span>{row.name}</span>,
      header: t("common.campaign"),
      width: "30%",
    },
    {
      accessor: "budget",
      Cell: (row: CampaignTypeDTO) => (
        <CurrencyCell
          value={
            row.integrations?.reduce(
              (acc, integration) => acc + (integration.selectedMonth?.budget ?? 0),
              0
            ) ?? 0
          }
        />
      ),
      header: t("common.budget"),
      width: "30%",
      sortable: false,
    },
    {
      accessor: "spent",
      Cell: (row: CampaignTypeDTO) => {
        if (!row.integrations) return <span>–</span>;
        const spentValues = row.integrations.map((jb) => jb.selectedMonth?.spent);
        const allNull = spentValues.every((v) => v == null);
        if (allNull) return <span>–</span>;

        const totalSpent = row.integrations.reduce(
          (acc, integration) => acc + (integration.selectedMonth?.spent ?? 0),
          0
        );
        return <CurrencyCell value={totalSpent} />;
      },
      header: t("common.spent"),
      width: "40%",
      sortable: false,
    },
  ];

  const budgetPlanningJobBoardColumns: TableColumn<JobboardCampaignType>[] = [
    {
      accessor: "jobBoard",
      Cell: (row: JobboardCampaignType) => (
        <WithMarginBadge
          name={
            translateIntegrationDisplayName(
              row.integrationDisplayName ?? row.jobBoardName,
              t,
              i18n
            ) || ""
          }
          includeMargin={row.includeMargin}
        />
      ),
      header: t("common.jobBoard"),
      width: "28%",
    },
    {
      accessor: "budget",
      Cell: (row: JobboardCampaignType) => <CurrencyCell value={row.budget} />,
      header: t("common.budget"),
      width: "28%",
    },
    {
      accessor: "spent",
      Cell: (row: JobboardCampaignType) => {
        return row.spent === null ? (
          <span>-</span>
        ) : (
          <CurrencyCell
            value={row.spent}
            secondaryValue={
              row.includeMargin ? row.actualSpent ?? 0 : undefined
            }
          />
        );
      },
      header: t("common.spent"),
      width: "28%",
    },
    {
      header: t("common.actions"),
      width: "10%",
      sortable: false,
      textAlign: "right",
      Cell: (row: JobboardCampaignType) => {
        const isDisabled = !canEditSelected || row.isInvoiced;

        const menuItems = [
          {
            disable: isDisabled,
            icon: <Icons.AddIcon fontSize={24} />,
            label: t("actions.edit", { entity: t("common.forecast") }),
            action: () => {
              if (selectedYear && selectedMonth && row.clientIntegrationModuleId) {
                openPopup(
                  <EditForecastBudgetPopup
                    // The endpoint takes the full monthly budget and prorates server-side, so a
                    // prorated first month must seed the form with monthlyBudget, not the allocation.
                    budget={row.monthlyBudget ?? row.budget}
                    campaignId={row.campaignId}
                    clientId={row.clientId2}
                    clientIntegrationModuleId={row.clientIntegrationModuleId}
                    year={selectedYear}
                    month={selectedMonth.padStart(2, "0")}
                    onCancel={closePopup}
                    onSuccess={handleSuccess}
                  />
                );
              }
            },
          },
        ];

        return (
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Menu
              trigger={() => (
                <div
                  role="presentation"
                  onClick={(e) => isDisabled && e.stopPropagation()}
                  onKeyDown={(e) => isDisabled && e.stopPropagation()}
                  style={
                    isDisabled
                      ? {
                        opacity: 0.5,
                        cursor: "not-allowed",
                        display: "inline-flex",
                      }
                      : undefined
                  }
                >
                  <Button variant="ghost" size="icon-sm">
                    <Icons.EllipsisIcon fontSize={24} />
                  </Button>
                </div>
              )}
            >
              {menuItems.map((item) => (
                <MenuItem
                  disabled={item.disable}
                  icon={item.icon}
                  key={item.label}
                  label={item.label}
                  onClick={item.action}
                />
              ))}
            </Menu>
          </div>
        );
      },
    },
  ];

  return {
    budgetPlanningTableColumns,
    budgetPlanningJobBoardColumns,
  };
};
